import { invoiceKindOptions } from './InvoiceKind';
import type { InvoiceKind } from '../../shared/types';
// Finance's filter sheet at ≤880px (mobile overhaul Phase C §8.1 A/B). The
// shared `FilterSheet` can't carry saved views or a "+ Save current" row, so
// Finance gets a page-scoped sheet the same way Projects did — same chrome
// classes (`.fsheet-*`, `.chiprow`, `.sec-l`), different contents.
//
// It edits a draft copy: nothing reaches the list until "Show N …", so a
// dismissed sheet leaves the live filters alone.

import { useState } from 'react';
import { FieldSheet } from './FieldSheet';
import { PickerSheet } from './PickerSheet';
import { Sheet, SheetButton, SheetSection } from './Sheet';
import type { SavedViewItem } from './ViewChips';
import { paramsEqual } from '../../shared/savedViews';
import { EXPENSE_CATEGORIES } from '../../shared/types';
import { categoryLabel, financeFromSaved, financeSavedParams, type FinanceFilters } from '../lib/financeListMeta';

export function FinanceFilterSheet({
  draft,
  setDraft,
  clients,
  views,
  count,
  onClose,
  onApply,
  onSave,
}: {
  draft: FinanceFilters;
  setDraft: (value: FinanceFilters) => void;
  clients: { id: string; name: string }[];
  views: SavedViewItem[];
  /** Rows the draft would show — the footer names it. */
  count: number;
  onClose: () => void;
  onApply: () => void;
  onSave: (name: string) => void;
}) {
  const [pickClient, setPickClient] = useState(false);
  const [save, setSave] = useState(false);
  const expenses = draft.tab === 'expenses';
  const noun = expenses ? 'expense' : 'invoice';

  return (
    <Sheet
      open
      variant="half"
      title="Filter"
      onClose={onClose}
      className="fsheet finance-filters"
      rightAction={
        <SheetButton tone="accent" onClick={() => setDraft({ ...draft, query: '', kind: '', client: null, category: null, unpaid: false, undated: false })}>
          Clear
        </SheetButton>
      }
      footer={
        <button type="button" className="btn primary" onClick={onApply}>
          Show {count} {count === 1 ? noun : `${noun}s`}
        </button>
      }
    >
      <SheetSection>Saved views</SheetSection>
      <div className="chiprow">
        {views.map((v) => (
          <button
            key={v.id}
            type="button"
            className={`c${paramsEqual(financeSavedParams(draft), v.params) ? ' on' : ''}`}
            onClick={() => setDraft(financeFromSaved(v.params))}
          >
            ★ {v.name}
          </button>
        ))}
        <button type="button" className="c" onClick={() => setSave(true)}>
          + Save current
        </button>
      </div>

      {expenses ? (
        <>
          <SheetSection>Category</SheetSection>
          <div className="chiprow">
            {['', ...EXPENSE_CATEGORIES].map((category) => (
              <button
                key={category || 'any'}
                type="button"
                className={`c${(draft.category ?? '') === category ? ' on' : ''}`}
                aria-pressed={(draft.category ?? '') === category}
                onClick={() => setDraft({ ...draft, category: category || null })}
              >
                {category ? categoryLabel(category) : 'Any'}
              </button>
            ))}
          </div>
        </>
      ) : (
        <>
          <SheetSection>Kind</SheetSection>
          <div className="chiprow">{[{ value: '', label: 'All' }, ...invoiceKindOptions].map(k => <button key={k.value} type="button"
            className={`c${(draft.kind ?? '') === k.value ? ' on' : ''}`} aria-pressed={(draft.kind ?? '') === k.value}
            onClick={() => setDraft({ ...draft, kind: k.value as InvoiceKind | '' })}>{k.label}</button>)}</div>
          <SheetSection>Client</SheetSection>
          <div className="fsheet-field">
            <button
              type="button"
              className={`fsheet-trigger${draft.client === null ? ' empty' : ''}`}
              onClick={() => setPickClient(true)}
            >
              <span>{clients.find((c) => c.id === draft.client)?.name ?? 'All clients'}</span>
              <span className="chev" aria-hidden="true">
                ▾
              </span>
            </button>
          </div>
        </>
      )}

      {draft.query && (
        <div className="chiprow">
          <button type="button" className="c on" onClick={() => setDraft({ ...draft, query: '' })}>
            Search: {draft.query} ×
          </button>
        </div>
      )}

      {pickClient && (
        <PickerSheet
          open
          title="Client"
          searchable
          items={[{ value: '', label: 'All clients' }, ...clients.map((c) => ({ value: c.id, label: c.name }))]}
          value={draft.client ?? ''}
          onSelect={(client) => setDraft({ ...draft, client: client || null })}
          onClose={() => setPickClient(false)}
        />
      )}
      {save && (
        <FieldSheet
          open
          kind="text"
          title="Save current view"
          value=""
          onSave={(name) => {
            if (name.trim()) onSave(name.trim());
          }}
          onClose={() => setSave(false)}
        />
      )}
    </Sheet>
  );
}
